import {
  FETCH_WELD_HEALTH,
  FETCH_WELD_HEALTH_SUCCESS,
  FETCH_WELD_HEALTH_FAILURE,
  FETCH_WELD_ANALYTICS_BUNDLE,
  FETCH_WELD_ANALYTICS_BUNDLE_SUCCESS,
  FETCH_WELD_ANALYTICS_BUNDLE_FAILURE,
  FETCH_WELD_ANALYTICS_BUNDLE_CANCELLED,
  FETCH_WELD_ANALYTICS_PRODUCTION,
  FETCH_WELD_ANALYTICS_PRODUCTION_SUCCESS,
  FETCH_WELD_ANALYTICS_PRODUCTION_FAILURE,
  FETCH_WELD_ANALYTICS_PRODUCTION_CANCELLED,
  FETCH_WELD_DETAIL,
  FETCH_WELD_DETAIL_SUCCESS,
  FETCH_WELD_DETAIL_FAILURE,
  TRIGGER_WELD_PIPELINE,
  TRIGGER_WELD_PIPELINE_SUCCESS,
  TRIGGER_WELD_PIPELINE_FAILURE,
  CLEAR_WELD_ANALYTICS,
  FETCH_INSPECTION_DATA,
  FETCH_INSPECTION_DATA_SUCCESS,
  FETCH_INSPECTION_DATA_FAILURE,
  FETCH_INSPECTION_PASSES,
  FETCH_INSPECTION_PASSES_SUCCESS,
  FETCH_INSPECTION_PASSES_FAILURE,
  LIVE_CHECK_ANOMALY,
  LIVE_CHECK_ANOMALY_SUCCESS,
  LIVE_CHECK_ANOMALY_FAILURE,
  LIVE_CHECK_INDICATION,
  LIVE_CHECK_INDICATION_SUCCESS,
  LIVE_CHECK_INDICATION_FAILURE,
  LIVE_PREDICT,
  LIVE_PREDICT_SUCCESS,
  LIVE_PREDICT_FAILURE,
  LIVE_SCORE_PASS,
  LIVE_SCORE_PASS_SUCCESS,
  LIVE_SCORE_PASS_FAILURE,
  FETCH_PIPELINE_EXECUTIONS,
  FETCH_PIPELINE_EXECUTIONS_SUCCESS,
  FETCH_PIPELINE_EXECUTIONS_FAILURE,
} from './types';

const initialState = {
  health: null,
  bundle: null,
  filters: null,
  weldDetail: null,
  productionRows: [],
  productionLoading: false,
  productionError: null,
  productionPartialErrors: [],
  statusCalculationMethod: 'Instantaneous',
  activeRequestId: null,
  isFetchingHealth: false,
  isFetching: false,
  isFetchingDetail: false,
  isTriggeringPipeline: false,
  error: null,
  healthError: null,
  detailError: null,
  pipelineError: null,
  pipelineResult: null,
  inspection: {
    jobs: [],
    welds: [],
    passesByKey: {},
    loading: false,
    passesLoading: false,
    error: null,
    ndtMap: {},
  },
  liveScoring: {
    byKey: {},
  },
  pipelineExecutions: [],
  pipelineExecutionsLoading: false,
  pipelineExecutionsError: null,
};

function isCurrentRequest(state, metaOrPayload) {
  const requestId = metaOrPayload?.requestId;
  if (state.activeRequestId == null) {
    return requestId == null;
  }
  if (requestId == null) return true;
  return state.activeRequestId === requestId;
}

function liveEntry(state, weldKey) {
  return state.liveScoring.byKey[weldKey] || {};
}

function setLiveEntry(state, weldKey, patch) {
  const prev = liveEntry(state, weldKey);
  return {
    ...state,
    liveScoring: {
      byKey: {
        ...state.liveScoring.byKey,
        [weldKey]: { ...prev, ...patch },
      },
    },
  };
}

function setLiveLoading(state, weldKey, field, loading) {
  const prev = liveEntry(state, weldKey);
  return setLiveEntry(state, weldKey, {
    loading: { ...(prev.loading || {}), [field]: loading },
  });
}

function clearLiveError(state, weldKey, field) {
  const prev = liveEntry(state, weldKey);
  const errors = { ...(prev.errors || {}) };
  delete errors[field];
  return setLiveEntry(state, weldKey, { errors });
}

function setLiveError(state, weldKey, field, error) {
  const prev = liveEntry(state, weldKey);
  return setLiveEntry(state, weldKey, {
    errors: { ...(prev.errors || {}), [field]: error },
  });
}

const weldAnalyticsReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_WELD_HEALTH:
      return { ...state, isFetchingHealth: true, healthError: null };
    case FETCH_WELD_HEALTH_SUCCESS:
      return { ...state, isFetchingHealth: false, health: action.payload, healthError: null };
    case FETCH_WELD_HEALTH_FAILURE:
      return { ...state, isFetchingHealth: false, healthError: action.error };

    case FETCH_WELD_ANALYTICS_BUNDLE: {
      const requestId = action.payload?.requestId ?? state.activeRequestId;
      return {
        ...state,
        isFetching: true,
        error: null,
        activeRequestId: requestId,
      };
    }
    case FETCH_WELD_ANALYTICS_BUNDLE_SUCCESS: {
      if (!isCurrentRequest(state, action.payload)) return state;
      return {
        ...state,
        isFetching: false,
        error: null,
        bundle: action.payload,
        filters: action.payload?.filters ?? null,
      };
    }
    case FETCH_WELD_ANALYTICS_BUNDLE_FAILURE: {
      if (!isCurrentRequest(state, action.meta)) return state;
      return {
        ...state,
        isFetching: false,
        error: action.error,
      };
    }
    case FETCH_WELD_ANALYTICS_BUNDLE_CANCELLED: {
      if (!isCurrentRequest(state, action.meta)) return state;
      return { ...state, isFetching: false };
    }

    case FETCH_WELD_ANALYTICS_PRODUCTION: {
      const requestId = action.payload?.requestId ?? state.activeRequestId;
      return {
        ...state,
        productionLoading: true,
        productionError: null,
        activeRequestId: requestId,
      };
    }
    case FETCH_WELD_ANALYTICS_PRODUCTION_SUCCESS: {
      if (!isCurrentRequest(state, action.payload)) return state;
      return {
        ...state,
        productionLoading: false,
        productionError: null,
        productionRows: action.payload?.weldData ?? [],
        productionPartialErrors: action.payload?.partialErrors ?? [],
        statusCalculationMethod: action.payload?.statusCalculationMethod || 'Instantaneous',
      };
    }
    case FETCH_WELD_ANALYTICS_PRODUCTION_FAILURE: {
      if (!isCurrentRequest(state, action.meta)) return state;
      return {
        ...state,
        productionLoading: false,
        productionError: action.error,
      };
    }
    case FETCH_WELD_ANALYTICS_PRODUCTION_CANCELLED: {
      if (!isCurrentRequest(state, action.meta)) return state;
      return { ...state, productionLoading: false };
    }

    case FETCH_WELD_DETAIL:
      return { ...state, isFetchingDetail: true, detailError: null };
    case FETCH_WELD_DETAIL_SUCCESS:
      return { ...state, isFetchingDetail: false, weldDetail: action.payload, detailError: null };
    case FETCH_WELD_DETAIL_FAILURE:
      return { ...state, isFetchingDetail: false, detailError: action.error, weldDetail: null };

    case TRIGGER_WELD_PIPELINE:
      return { ...state, isTriggeringPipeline: true, pipelineError: null, pipelineResult: null };
    case TRIGGER_WELD_PIPELINE_SUCCESS:
      return {
        ...state,
        isTriggeringPipeline: false,
        pipelineResult: action.payload,
        pipelineError: null,
      };
    case TRIGGER_WELD_PIPELINE_FAILURE:
      return { ...state, isTriggeringPipeline: false, pipelineError: action.error };

    case FETCH_INSPECTION_DATA:
      return {
        ...state,
        inspection: { ...state.inspection, loading: true, error: null },
      };
    case FETCH_INSPECTION_DATA_SUCCESS:
      return {
        ...state,
        inspection: {
          ...state.inspection,
          loading: false,
          jobs: action.payload.jobs ?? [],
          welds: action.payload.welds ?? [],
          ndtMap: action.payload.ndtMap ?? {},
          error: null,
        },
      };
    case FETCH_INSPECTION_DATA_FAILURE:
      return {
        ...state,
        inspection: { ...state.inspection, loading: false, error: action.error },
      };

    case FETCH_INSPECTION_PASSES:
      return {
        ...state,
        inspection: { ...state.inspection, passesLoading: true },
      };
    case FETCH_INSPECTION_PASSES_SUCCESS:
      return {
        ...state,
        inspection: {
          ...state.inspection,
          passesLoading: false,
          passesByKey: {
            ...state.inspection.passesByKey,
            [action.payload.weldKey]: action.payload.passes,
          },
        },
      };
    case FETCH_INSPECTION_PASSES_FAILURE:
      return {
        ...state,
        inspection: { ...state.inspection, passesLoading: false, error: action.error },
      };

    case LIVE_CHECK_ANOMALY: {
      const weldKey = action.payload?.weldKey ?? null;
      if (!weldKey) return state;
      return clearLiveError(setLiveLoading(state, weldKey, 'anomaly', true), weldKey, 'anomaly');
    }
    case LIVE_CHECK_INDICATION: {
      const weldKey = action.payload?.weldKey ?? null;
      if (!weldKey) return state;
      const withLoading = setLiveLoading(
        clearLiveError(state, weldKey, 'indication'),
        weldKey,
        'indication',
        true,
      );
      return setLiveEntry(withLoading, weldKey, { indication: undefined });
    }
    case LIVE_PREDICT:
    case LIVE_SCORE_PASS: {
      const weldKey = action.payload?.weldKey ?? null;
      if (!weldKey) return state;
      return state;
    }
    case LIVE_CHECK_ANOMALY_SUCCESS:
    case LIVE_CHECK_INDICATION_SUCCESS:
    case LIVE_PREDICT_SUCCESS:
    case LIVE_SCORE_PASS_SUCCESS: {
      const key = action.payload.weldKey;
      const field =
        action.type === LIVE_CHECK_ANOMALY_SUCCESS
          ? 'anomaly'
          : action.type === LIVE_CHECK_INDICATION_SUCCESS
            ? 'indication'
            : action.type === LIVE_PREDICT_SUCCESS
              ? 'predict'
              : 'realtime';
      const withResult = setLiveEntry(state, key, {
        [field]: action.payload.result,
        updatedAt: action.payload.updatedAt,
      });
      return setLiveLoading(
        clearLiveError(withResult, key, field),
        key,
        field,
        false,
      );
    }
    case LIVE_CHECK_ANOMALY_FAILURE: {
      const weldKey = action.payload?.weldKey ?? action.meta?.weldKey ?? null;
      if (!weldKey) return state;
      const withLoading = setLiveLoading(state, weldKey, 'anomaly', false);
      return setLiveError(withLoading, weldKey, 'anomaly', action.error);
    }
    case LIVE_CHECK_INDICATION_FAILURE: {
      const weldKey = action.payload?.weldKey ?? action.meta?.weldKey ?? null;
      if (!weldKey) return state;
      const withLoading = setLiveLoading(state, weldKey, 'indication', false);
      return setLiveError(withLoading, weldKey, 'indication', action.error);
    }
    case LIVE_PREDICT_FAILURE: {
      const weldKey = action.payload?.weldKey ?? action.meta?.weldKey ?? null;
      if (!weldKey) return state;
      const withLoading = setLiveLoading(state, weldKey, 'predict', false);
      return setLiveError(withLoading, weldKey, 'predict', action.error);
    }
    case LIVE_SCORE_PASS_FAILURE: {
      const weldKey = action.payload?.weldKey ?? action.meta?.weldKey ?? null;
      if (!weldKey) return state;
      const withLoading = setLiveLoading(state, weldKey, 'realtime', false);
      return setLiveError(withLoading, weldKey, 'realtime', action.error);
    }

    case FETCH_PIPELINE_EXECUTIONS:
      return {
        ...state,
        pipelineExecutionsLoading: true,
        pipelineExecutionsError: null,
      };
    case FETCH_PIPELINE_EXECUTIONS_SUCCESS:
      return {
        ...state,
        pipelineExecutionsLoading: false,
        pipelineExecutions: action.payload ?? [],
        pipelineExecutionsError: null,
      };
    case FETCH_PIPELINE_EXECUTIONS_FAILURE:
      return {
        ...state,
        pipelineExecutionsLoading: false,
        pipelineExecutionsError: action.error,
      };

    case CLEAR_WELD_ANALYTICS:
      return { ...initialState, liveScoring: state.liveScoring };

    default:
      return state;
  }
};

export default weldAnalyticsReducer;
export { initialState };
